/**
 * Report Exports Component
 * 
 * Comprehensive export management interface with download history,
 * format options, and bulk operations for exported reports
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Button, 
    Form, 
    Modal, 
    Dropdown,
    Badge,
    Alert,
    Spinner,
    Table,
    InputGroup,
    ProgressBar,
    Accordion
} from 'react-bootstrap';
import { 
    BsDownload, 
    BsFileEarmark,
    BsFiletypePdf,
    BsFiletypeXlsx,
    BsFiletypeCsv,
    BsSearch,
    BsFilter,
    BsTrash,
    BsEye,
    BsCloudDownload,
    BsArchive,
    BsCheck,
    BsX,
    BsClock,
    BsExclamationTriangle,
    BsInfoCircle,
    BsCheckCircle,
    BsHourglassSplit,
    BsThreeDotsVertical,
    BsShare,
    BsFolder,
    BsCalendar,
    BsArrowUp,
    BsArrowDown
} from 'react-icons/bs';
import { toast } from 'react-hot-toast';
import { ErrorBoundary, ServiceStatus, ReportingFallback } from './shared';
import reportingService from '../../services/reportingService';

const ReportExports = () => {
    // Local state
    const [exports, setExports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterFormat, setFilterFormat] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedExports, setSelectedExports] = useState([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentExport, setCurrentExport] = useState(null);
    const [exportProgress, setExportProgress] = useState({});
    const [statistics, setStatistics] = useState(null);
    const [availableReports, setAvailableReports] = useState([]);
    const [newExportForm, setNewExportForm] = useState({
        reportId: '',
        format: 'pdf',
        options: {
            include_charts: true,
            include_summary: true,
            page_orientation: 'portrait'
        }
    });
    
    // Export formats configuration
    const exportFormats = [
        { 
            value: 'pdf', 
            label: 'PDF Document', 
            icon: BsFiletypePdf, 
            color: 'danger',
            description: 'Formatted PDF with charts and styling'
        },
        { 
            value: 'excel', 
            label: 'Excel Workbook', 
            icon: BsFiletypeXlsx, 
            color: 'success',
            description: 'Excel file with data and charts'
        },
        { 
            value: 'csv', 
            label: 'CSV File', 
            icon: BsFiletypeCsv, 
            color: 'info',
            description: 'Comma-separated values for data analysis'
        }
    ];
    
    // Export statuses
    const exportStatuses = [
        { value: 'pending', label: 'Pending', color: 'warning', icon: BsHourglassSplit },
        { value: 'processing', label: 'Processing', color: 'info', icon: BsClock },
        { value: 'completed', label: 'Completed', color: 'success', icon: BsCheckCircle },
        { value: 'failed', label: 'Failed', color: 'danger', icon: BsX },
        { value: 'cancelled', label: 'Cancelled', color: 'secondary', icon: BsX }
    ];
    
    // Load initial data
    useEffect(() => {
        loadExports();
        loadStatistics();
        loadAvailableReports();
    }, []);
    
    // Reload exports when filters change
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            loadExports();
        }, 300);
        
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, filterFormat, filterStatus, sortBy, sortOrder]);
    
    // Load exports with current filters
    const loadExports = async () => {
        try {
            setLoading(true);
            const params = {
                search: searchTerm || undefined,
                format: filterFormat !== 'all' ? filterFormat : undefined,
                status: filterStatus !== 'all' ? filterStatus : undefined,
                sort_by: sortBy,
                sort_order: sortOrder,
                limit: 50
            };
            
            const response = await reportingService.getExports(params);
            if (response.success) {
                setExports(response.data);
            } else {
                toast.error('Failed to load exports');
            }
        } catch (error) {
            toast.error('Failed to load exports');
        } finally {
            setLoading(false);
        }
    };
    
    // Load export statistics
    const loadStatistics = async () => {
        try {
            const response = await reportingService.getExportStatistics();
            if (response.success) {
                setStatistics(response.data);
            }
        } catch (error) {
        }
    };
    
    // Load available reports for export
    const loadAvailableReports = async () => {
        try {
            const response = await reportingService.getReports({ status: 'completed', limit: 100 });
            if (response.success) {
                setAvailableReports(response.data);
            }
        } catch (error) {
        }
    };
    
    // Handle search
    const handleSearch = (term) => {
        setSearchTerm(term);
    };
    
    // Handle export selection
    const handleExportSelect = (exportId, isSelected) => {
        if (isSelected) {
            setSelectedExports(prev => [...prev, exportId]);
        } else {
            setSelectedExports(prev => prev.filter(id => id !== exportId));
        }
    };
    
    // Handle select all
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            setSelectedExports(filteredExports.map(e => e.id));
        } else {
            setSelectedExports([]);
        }
    };
    
    // Create new export
    const handleCreateExport = () => {
        setNewExportForm({
            reportId: '',
            format: 'pdf',
            options: {
                include_charts: true,
                include_summary: true,
                page_orientation: 'portrait'
            }
        });
        setShowExportModal(true);
    };
    
    // Submit new export
    const handleSubmitExport = async () => {
        try {
            if (!newExportForm.reportId) {
                toast.error('Please select a report to export');
                return;
            }
            
            const exportData = {
                report_id: newExportForm.reportId,
                format: newExportForm.format,
                options: newExportForm.options
            };
            
            const response = await reportingService.createExport(exportData);
            if (response.success) {
                toast.success('Export started successfully');
                setShowExportModal(false);
                loadExports();
                
                // Start progress monitoring
                monitorExportProgress(response.data.id);
            } else {
                toast.error('Failed to start export');
            }
        } catch (error) {
            toast.error('Failed to start export');
        }
    };
    
    // Monitor export progress
    const monitorExportProgress = async (exportId) => {
        const checkProgress = async () => {
            try {
                const response = await reportingService.getExportStatus(exportId);
                if (response.success) {
                    const status = response.data;
                    setExportProgress(prev => ({
                        ...prev,
                        [exportId]: status
                    }));
                    
                    if (status.status === 'completed') {
                        toast.success('Export completed successfully');
                        loadExports();
                    } else if (status.status === 'failed') {
                        toast.error('Export failed');
                        loadExports();
                    } else if (status.status === 'processing') {
                        // Continue monitoring
                        setTimeout(checkProgress, 2000);
                    }
                }
            } catch (error) {
            }
        };
        
        checkProgress();
    };
    
    // Download export
    const handleDownloadExport = async (exportItem) => {
        try {
            const response = await reportingService.downloadExport(exportItem.id);
            if (response.success) {
                // Create download link
                const blob = new Blob([response.data], { 
                    type: getContentType(exportItem.format) 
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = exportItem.filename || `export_${exportItem.id}.${exportItem.format}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                toast.success('Export downloaded successfully');
            } else {
                toast.error('Failed to download export');
            }
        } catch (error) {
            toast.error('Failed to download export');
        }
    };
    
    // Get content type for download
    const getContentType = (format) => {
        const contentTypes = {
            pdf: 'application/pdf',
            excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            csv: 'text/csv'
        };
        return contentTypes[format] || 'application/octet-stream';
    };
    
    // Delete export
    const handleDeleteExport = (exportItem) => {
        setCurrentExport(exportItem);
        setShowDeleteModal(true);
    };
    
    // Confirm delete
    const confirmDelete = async () => {
        try {
            const response = await reportingService.deleteExport(currentExport.id);
            if (response.success) {
                toast.success('Export deleted successfully');
                setShowDeleteModal(false);
                setCurrentExport(null);
                loadExports();
            } else {
                toast.error('Failed to delete export');
            }
        } catch (error) {
            toast.error('Failed to delete export');
        }
    };
    
    // Bulk delete
    const handleBulkDelete = async () => {
        if (selectedExports.length === 0) {
            toast.error('Please select exports to delete');
            return;
        }
        
        if (window.confirm(`Are you sure you want to delete ${selectedExports.length} exports?`)) {
            try {
                await Promise.all(selectedExports.map(id => reportingService.deleteExport(id)));
                toast.success(`${selectedExports.length} exports deleted successfully`);
                setSelectedExports([]);
                loadExports();
            } catch (error) {
                toast.error('Failed to delete some exports');
            }
        }
    };
    
    // Bulk download
    const handleBulkDownload = async () => {
        if (selectedExports.length === 0) {
            toast.error('Please select exports to download');
            return;
        }
        
        try {
            const response = await reportingService.bulkDownloadExports(selectedExports);
            if (response.success) {
                // Download the zip file
                const blob = new Blob([response.data], { type: 'application/zip' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `bulk_exports_${new Date().getTime()}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                toast.success('Bulk download started');
            } else {
                toast.error('Failed to start bulk download');
            }
        } catch (error) {
            toast.error('Failed to start bulk download');
        }
    };
    
    // Update export form field
    const updateExportForm = (field, value) => {
        setNewExportForm(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    // Update export options
    const updateExportOptions = (option, value) => {
        setNewExportForm(prev => ({
            ...prev,
            options: {
                ...prev.options,
                [option]: value
            }
        }));
    };
    
    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };
    
    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Get format icon
    const getFormatIcon = (format) => {
        const formatConfig = exportFormats.find(f => f.value === format);
        return formatConfig ? formatConfig.icon : BsFileEarmark;
    };
    
    // Get format color
    const getFormatColor = (format) => {
        const formatConfig = exportFormats.find(f => f.value === format);
        return formatConfig ? formatConfig.color : 'secondary';
    };
    
    // Get status config
    const getStatusConfig = (status) => {
        return exportStatuses.find(s => s.value === status) || exportStatuses[0];
    };
    
    // Filter exports based on current filters
    const filteredExports = exports.filter(exportItem => {
        const matchesSearch = !searchTerm || 
            exportItem.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exportItem.report_name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesFormat = filterFormat === 'all' || exportItem.format === filterFormat;
        const matchesStatus = filterStatus === 'all' || exportItem.status === filterStatus;
        
        return matchesSearch && matchesFormat && matchesStatus;
    });
    
    return (
        <ErrorBoundary fallbackMessage="Unable to load report exports. Please refresh the page or contact support.">
            <ServiceStatus 
                fallbackComponent={<ReportingFallback />}
                showStatus={true}
                autoRetry={false}
            >
                <Container fluid className="p-4">
                    {/* Header */}
                    <Row className="align-items-center mb-4">
                        <Col lg={8}>
                            <div>
                                <h2 className="text-primary fw-bold mb-1">Report Exports</h2>
                                <p className="text-muted mb-0">
                                    Manage exported reports and download files in various formats
                                </p>
                            </div>
                        </Col>
                        <Col lg={4} className="text-end">
                            <div className="d-flex gap-2">
                                <Button 
                                    variant="outline-info"
                                    size="sm"
                                    onClick={handleBulkDownload}
                                    disabled={selectedExports.length === 0}
                                >
                                    <BsCloudDownload className="me-1" />
                                    Download ({selectedExports.length})
                                </Button>
                                <Button 
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={handleBulkDelete}
                                    disabled={selectedExports.length === 0}
                                >
                                    <BsTrash className="me-1" />
                                    Delete ({selectedExports.length})
                                </Button>
                                <Button 
                                    variant="primary"
                                    size="sm"
                                    onClick={handleCreateExport}
                                >
                                    <BsDownload className="me-1" />
                                    New Export
                                </Button>
                            </div>
                        </Col>
                    </Row>
                    
                    {/* Statistics Cards */}
                    {statistics && (
                        <Row className="mb-4">
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsFileEarmark className="h3 text-primary me-2" />
                                            <span className="h4 mb-0">{statistics.total_exports || 0}</span>
                                        </div>
                                        <small className="text-muted">Total Exports</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsCheckCircle className="h3 text-success me-2" />
                                            <span className="h4 mb-0">{statistics.completed_exports || 0}</span>
                                        </div>
                                        <small className="text-muted">Completed</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsClock className="h3 text-info me-2" />
                                            <span className="h4 mb-0">{statistics.processing_exports || 0}</span>
                                        </div>
                                        <small className="text-muted">Processing</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <BsArchive className="h3 text-warning me-2" />
                                            <span className="h4 mb-0">{statistics.total_size ? formatFileSize(statistics.total_size) : '0 MB'}</span>
                                        </div>
                                        <small className="text-muted">Total Size</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    
                    {/* Controls */}
                    <Row className="mb-4">
                        <Col>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="py-3">
                                    <Row className="align-items-center">
                                        <Col lg={4}>
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <BsSearch />
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search exports..."
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col lg={2}>
                                            <Form.Select
                                                value={filterFormat}
                                                onChange={(e) => setFilterFormat(e.target.value)}
                                            >
                                                <option value="all">All Formats</option>
                                                {exportFormats.map(format => (
                                                    <option key={format.value} value={format.value}>
                                                        {format.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col lg={2}>
                                            <Form.Select
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="all">All Status</option>
                                                {exportStatuses.map(status => (
                                                    <option key={status.value} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col lg={2}>
                                            <Dropdown>
                                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                    <BsFilter className="me-1" />
                                                    Sort by {sortBy === 'created_at' ? 'Date' : 'Name'}
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => { setSortBy('created_at'); setSortOrder('desc'); }}>
                                                        <BsArrowDown className="me-1" />Newest First
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => { setSortBy('created_at'); setSortOrder('asc'); }}>
                                                        <BsArrowUp className="me-1" />Oldest First
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => { setSortBy('filename'); setSortOrder('asc'); }}>
                                                        <BsArrowUp className="me-1" />Name A-Z
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => { setSortBy('filename'); setSortOrder('desc'); }}>
                                                        <BsArrowDown className="me-1" />Name Z-A
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </Col>
                                        <Col lg={2} className="text-end">
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={loadExports}
                                                disabled={loading}
                                            >
                                                {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    
                    {/* Exports Table */}
                    <Row>
                        <Col>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-transparent border-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">Export History ({filteredExports.length})</h6>
                                        {filteredExports.length > 0 && (
                                            <Form.Check
                                                type="checkbox"
                                                label="Select All"
                                                checked={selectedExports.length === filteredExports.length && filteredExports.length > 0}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                        )}
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" className="text-primary" />
                                            <p className="mt-2 text-muted">Loading exports...</p>
                                        </div>
                                    ) : filteredExports.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table hover className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th width="40"></th>
                                                        <th>File</th>
                                                        <th>Report</th>
                                                        <th>Format</th>
                                                        <th>Status</th>
                                                        <th>Size</th>
                                                        <th>Created</th>
                                                        <th width="120">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredExports.map(exportItem => {
                                                        const FormatIcon = getFormatIcon(exportItem.format);
                                                        const statusConfig = getStatusConfig(exportItem.status);
                                                        const StatusIcon = statusConfig.icon;
                                                        const progress = exportProgress[exportItem.id];
                                                        
                                                        return (
                                                            <tr key={exportItem.id}>
                                                                <td>
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        checked={selectedExports.includes(exportItem.id)}
                                                                        onChange={(e) => handleExportSelect(exportItem.id, e.target.checked)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <FormatIcon className={`text-${getFormatColor(exportItem.format)} me-2`} />
                                                                        <div>
                                                                            <div className="fw-semibold">
                                                                                {exportItem.filename || `export_${exportItem.id}`}
                                                                            </div>
                                                                            {exportItem.description && (
                                                                                <small className="text-muted">{exportItem.description}</small>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <span className="fw-semibold">
                                                                            {exportItem.report_name || `Report ${exportItem.report_id}`}
                                                                        </span>
                                                                        <br />
                                                                        <small className="text-muted">ID: {exportItem.report_id}</small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <Badge bg={getFormatColor(exportItem.format)}>
                                                                        {exportItem.format.toUpperCase()}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <StatusIcon className={`text-${statusConfig.color} me-1`} />
                                                                        <Badge bg={statusConfig.color}>
                                                                            {statusConfig.label}
                                                                        </Badge>
                                                                    </div>
                                                                    {progress && progress.status === 'processing' && (
                                                                        <div className="mt-1">
                                                                            <ProgressBar 
                                                                                now={progress.progress || 0} 
                                                                                size="sm" 
                                                                                label={`${progress.progress || 0}%`}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted">
                                                                        {formatFileSize(exportItem.file_size)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <span className="text-muted small">
                                                                            {formatDate(exportItem.created_at)}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <Dropdown>
                                                                        <Dropdown.Toggle variant="link" size="sm" className="text-muted">
                                                                            <BsThreeDotsVertical />
                                                                        </Dropdown.Toggle>
                                                                        <Dropdown.Menu>
                                                                            <Dropdown.Item 
                                                                                onClick={() => handleDownloadExport(exportItem)}
                                                                                disabled={exportItem.status !== 'completed'}
                                                                            >
                                                                                <BsDownload className="me-2" />
                                                                                Download
                                                                            </Dropdown.Item>
                                                                            <Dropdown.Item>
                                                                                <BsShare className="me-2" />
                                                                                Share
                                                                            </Dropdown.Item>
                                                                            <Dropdown.Divider />
                                                                            <Dropdown.Item 
                                                                                onClick={() => handleDeleteExport(exportItem)}
                                                                                className="text-danger"
                                                                            >
                                                                                <BsTrash className="me-2" />
                                                                                Delete
                                                                            </Dropdown.Item>
                                                                        </Dropdown.Menu>
                                                                    </Dropdown>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-5">
                                            <BsFileEarmark className="display-4 text-muted mb-3" />
                                            <h5 className="text-muted">No Exports Found</h5>
                                            <p className="text-muted mb-4">
                                                {searchTerm || filterFormat !== 'all' || filterStatus !== 'all'
                                                    ? 'No exports match your current filters. Try adjusting your search criteria.'
                                                    : 'You haven\'t created any exports yet. Export your first report to get started.'
                                                }
                                            </p>
                                            <Button variant="primary" onClick={handleCreateExport}>
                                                <BsDownload className="me-1" />
                                                Create Your First Export
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                
                {/* Create Export Modal */}
                <Modal show={showExportModal} onHide={() => setShowExportModal(false)} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Create New Export</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Select Report *</Form.Label>
                                <Form.Select
                                    value={newExportForm.reportId}
                                    onChange={(e) => updateExportForm('reportId', e.target.value)}
                                >
                                    <option value="">Choose a report to export...</option>
                                    {availableReports.map(report => (
                                        <option key={report.id} value={report.id}>
                                            {report.name || `Report ${report.id}`} - {formatDate(report.created_at)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Export Format *</Form.Label>
                                <div className="row g-3">
                                    {exportFormats.map(format => {
                                        const FormatIcon = format.icon;
                                        return (
                                            <div key={format.value} className="col-md-4">
                                                <Card 
                                                    className={`format-option border ${newExportForm.format === format.value ? 'border-primary' : 'border-light'}`}
                                                    onClick={() => updateExportForm('format', format.value)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <Card.Body className="text-center py-3">
                                                        <FormatIcon className={`h3 text-${format.color} mb-2`} />
                                                        <h6 className={newExportForm.format === format.value ? 'text-primary' : ''}>
                                                            {format.label}
                                                        </h6>
                                                        <small className="text-muted">{format.description}</small>
                                                        {newExportForm.format === format.value && (
                                                            <div className="mt-2">
                                                                <Badge bg="primary">Selected</Badge>
                                                            </div>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Form.Group>
                            
                            <Accordion>
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>Export Options</Accordion.Header>
                                    <Accordion.Body>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Check
                                                        type="switch"
                                                        label="Include Charts"
                                                        checked={newExportForm.options.include_charts}
                                                        onChange={(e) => updateExportOptions('include_charts', e.target.checked)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Check
                                                        type="switch"
                                                        label="Include Summary"
                                                        checked={newExportForm.options.include_summary}
                                                        onChange={(e) => updateExportOptions('include_summary', e.target.checked)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        
                                        {newExportForm.format === 'pdf' && (
                                            <Form.Group className="mb-3">
                                                <Form.Label>Page Orientation</Form.Label>
                                                <Form.Select
                                                    value={newExportForm.options.page_orientation}
                                                    onChange={(e) => updateExportOptions('page_orientation', e.target.value)}
                                                >
                                                    <option value="portrait">Portrait</option>
                                                    <option value="landscape">Landscape</option>
                                                </Form.Select>
                                            </Form.Group>
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSubmitExport} disabled={!newExportForm.reportId}>
                            <BsDownload className="me-1" />
                            Start Export
                        </Button>
                    </Modal.Footer>
                </Modal>
                
                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title className="text-danger">
                            <BsExclamationTriangle className="me-2" />
                            Confirm Delete
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete the export <strong>"{currentExport?.filename}"</strong>?</p>
                        <Alert variant="warning" className="mb-0">
                            <small>
                                <strong>Warning:</strong> This action cannot be undone. 
                                The exported file will be permanently removed.
                            </small>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={confirmDelete}>
                            <BsTrash className="me-1" />
                            Delete Export
                        </Button>
                    </Modal.Footer>
                </Modal>
            </ServiceStatus>
        </ErrorBoundary>
    );
};

export default ReportExports;